/**
 * Owner: Masa Bayu
 * File: demo-author-earnings.ts
 * Date:Time: 2026-09-16 14:52
 * Description: Demo SaleRecord → EarningLedger · payout intent for T6-E5 UI.
 */

import {
  applyCommerceEarningSettlement,
  confirmOrderAfterSuccessfulPayment,
  createAuthorCapableAccount,
  createAuthorPayoutRequest,
  createPendingOrderDraft,
  createPaymentAttempt,
  createPaymentIntent,
  createQxreadAuthorAccount,
  createQxreadSession,
  projectAuthorEarningsSummary,
  projectEarningLedgerFromSaleRecords,
  projectSaleRecordsFromConfirmedOrders,
  type QxreadAuthorEarningsSummary,
  type QxreadCheckoutSession,
  type QxreadEarningLedgerEntry,
  type QxreadPayoutRequest,
} from '@qx/qxread-core';

const DEMO_ACCOUNT_ID = 'qxrdacc_demo_author';
const DEMO_NOW = '2026-09-16T06:52:00.000Z';
const DEMO_PUB = 'qxrdpub_demo_store';

function demoCheckout(): QxreadCheckoutSession {
  return Object.freeze({
    checkoutId: 'qxrdchk_demo_earn',
    cartId: 'qxrdcart_demo_earn',
    sessionKey: 'qxrdsess_demo_earn',
    state: 'ready' as const,
    totals: Object.freeze({
      currency: 'USD',
      sellerSubtotalMinorUnits: 1299,
      serviceFeeMinorUnits: 58,
      buyerTotalMinorUnits: 1357,
      affiliateContractMinorUnits: 65,
      referralContractMinorUnits: 65,
      rbsContractMinorUnits: 0,
      uniquePublicationCount: 1,
      ledgerLines: Object.freeze([]),
      attributions: Object.freeze([]),
    }),
    blockers: Object.freeze([] as string[]),
    createdAt: DEMO_NOW,
  });
}

let demoAccount = createAuthorCapableAccount({
  accountId: DEMO_ACCOUNT_ID,
  displayName: 'Demo Author',
});
let demoSession = createQxreadSession({
  state: 'authenticated',
  accountId: DEMO_ACCOUNT_ID,
  issuedAt: DEMO_NOW,
});
let demoAuthor = createQxreadAuthorAccount({
  accountId: DEMO_ACCOUNT_ID,
  displayName: 'Demo Author',
  slug: 'demo-author',
  createdAt: DEMO_NOW,
  updatedAt: DEMO_NOW,
});
let demoLedger: readonly QxreadEarningLedgerEntry[] = Object.freeze([]);
let demoSummary: QxreadAuthorEarningsSummary | null = null;
let demoPayout: QxreadPayoutRequest | null = null;

function ensureActors(): void {
  demoAccount = createAuthorCapableAccount({
    accountId: DEMO_ACCOUNT_ID,
    displayName: 'Demo Author',
  });
  demoSession = createQxreadSession({
    state: 'authenticated',
    accountId: DEMO_ACCOUNT_ID,
    issuedAt: DEMO_NOW,
  });
  demoAuthor = createQxreadAuthorAccount({
    accountId: DEMO_ACCOUNT_ID,
    displayName: 'Demo Author',
    slug: 'demo-author',
    createdAt: DEMO_NOW,
    updatedAt: DEMO_NOW,
  });
}

function demoSaleRecords() {
  const pending = createPendingOrderDraft(demoCheckout(), [
    {
      publicationId: DEMO_PUB,
      title: 'River Atlas',
      quantity: 1,
      sellerPriceMinorUnits: 1299,
      currency: 'USD',
    },
  ]);
  const intent = createPaymentIntent(pending, { idempotencyKey: 'demo_earn_pay' });
  const attempt = createPaymentAttempt(intent);
  const succeeded = Object.freeze({
    ...attempt,
    state: 'succeeded' as const,
    updatedAt: DEMO_NOW,
  });
  const confirmed = confirmOrderAfterSuccessfulPayment(pending, succeeded, {
    confirmedAt: DEMO_NOW,
  });
  return projectSaleRecordsFromConfirmedOrders({
    authorAccount: demoAuthor,
    authorOwnedPublicationIds: new Set([DEMO_PUB]),
    confirmedOrders: [confirmed],
    recordedAt: DEMO_NOW,
  });
}

export function resetDemoAuthorEarnings(): void {
  ensureActors();
  demoLedger = Object.freeze([]);
  demoSummary = null;
  demoPayout = null;
}

export function getDemoEarningLedger(): readonly QxreadEarningLedgerEntry[] {
  return demoLedger;
}

export function getDemoEarningsSummary(): QxreadAuthorEarningsSummary | null {
  return demoSummary;
}

export function getDemoPayoutRequest(): QxreadPayoutRequest | null {
  return demoPayout;
}

export function projectDemoAuthorEarnings(): string {
  const sales = demoSaleRecords();
  const pending = projectEarningLedgerFromSaleRecords({
    authorAccount: demoAuthor,
    saleRecords: sales,
    updatedAt: DEMO_NOW,
  });
  demoLedger = applyCommerceEarningSettlement({
    ledgerEntries: pending,
    settledAt: DEMO_NOW,
  });
  demoSummary = projectAuthorEarningsSummary({
    session: demoSession,
    account: demoAccount,
    authorAccount: demoAuthor,
    ledgerEntries: demoLedger,
    projectedAt: DEMO_NOW,
  });
  demoPayout = null;
  return `Earnings projected · available=${String(demoSummary.availableMinorUnits)} ${demoSummary.currency} · inventedByAuthorUi=false · executesPayoutInAuthorUi=false`;
}

export function requestDemoAuthorPayout(stepUpVerified: boolean): string {
  if (demoSummary === null || demoLedger.length === 0) {
    return 'Project earnings from confirmed sales first.';
  }
  if (!stepUpVerified) {
    return 'Step-up verification required for payout request.';
  }
  try {
    const result = createAuthorPayoutRequest({
      authorAccount: demoAuthor,
      ledgerEntries: demoLedger,
      amountMinorUnits: demoSummary.availableMinorUnits,
      stepUpVerified: true,
      requestedAt: DEMO_NOW,
    });
    demoLedger = result.ledgerEntries;
    demoPayout = result.payoutRequest;
    demoSummary = projectAuthorEarningsSummary({
      session: demoSession,
      account: demoAccount,
      authorAccount: demoAuthor,
      ledgerEntries: demoLedger,
      projectedAt: DEMO_NOW,
    });
    return `Payout requested · state=${demoPayout.state} · stripePayoutUnderCommerce=${String(demoPayout.stripePayoutUnderCommerce)} · executesPayoutInAuthorUi=false`;
  } catch (error) {
    return error instanceof Error ? error.message : 'Payout request failed.';
  }
}
